// src/components/modal/MenuItemDetails/utils/variantUtils.ts
import type { MenuItem } from "~/types/new_types";
import type { ItemCard } from "../types";
import { getFormName } from "~/utils/menuItemHelper";

/**
 * Checks if two variant objects are identical
 */
export function areVariantsEqual(
  variants1: Record<string, any>,
  variants2: Record<string, any>
): boolean {
  const keys1 = Object.keys(variants1).sort();
  const keys2 = Object.keys(variants2).sort();

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => {
    const val1 = variants1[key];
    const val2 = variants2[key];

    if (Array.isArray(val1) && Array.isArray(val2)) {
      return (
        val1.length === val2.length &&
        val1.every((item, index) => item === val2[index])
      );
    }

    return val1 === val2;
  });
}

/**
 * Extracts variants from all item cards in a normalized format
 */
export function extractVariantsFromCards(
  itemCards: ItemCard[],
  item: MenuItem,
  language: string
): any[] {
  return itemCards
    .map((card) => {
      const cardVariants: any[] = [];

      // Handle variants_and_options
      if (item.variants_and_options && Array.isArray(item.variants_and_options)) {
        item.variants_and_options.forEach((group) => {
          const groupId = group.id;
          const selectedValue = card.variants[groupId];

          if (selectedValue && Array.isArray(group.data)) {
            const selectedOption = group.data.find(
              (option: any) => option.name === selectedValue
            );
            if (selectedOption) {
              cardVariants.push({
                name: getFormName(group, language),
                key: groupId,
                variantType: "variants_and_options",
                actionType: group.action_type || "selection",
                children: [
                  {
                    name: selectedOption.name,
                    price: selectedOption.price || 0,
                    imageUrl: selectedOption.image_url || "",
                  },
                ],
                quantity: card.quantity,
              });
            }
          }
        });
      }

      // Handle meals_and_combos
      if (item.meals_and_combos && Array.isArray(item.meals_and_combos)) {
        item.meals_and_combos.forEach((combo) => {
          const comboId = combo.id;
          const selectedCombo = card.variants[comboId];

          if (selectedCombo) {
            cardVariants.push({
              name: getFormName(combo, language),
              key: comboId,
              variantType: "meals_and_combos",
              actionType: combo.action_type || "upgrade",
              children: [
                {
                  name: getFormName(combo, language),
                  price: combo.data.combo_price || 0,
                  imageUrl: "",
                  comboData: combo.data,
                },
              ],
              quantity: card.quantity,
            });
          }
        });
      }

      if (cardVariants.length > 0) {
        return cardVariants;
      } else {
        return [
          {
            name: "",
            children: [],
            quantity: card.quantity,
          },
        ];
      }
    })
    .flat();
}

/**
 * Gets total quantity from all cards
 */
export function getTotalCardQuantity(itemCards: ItemCard[]): number {
  return itemCards.reduce((total, card) => total + card.quantity, 0);
}

/**
 * Extracts variants for a specific card in the required format
 */
export function extractVariantsForCard(
  itemCards: ItemCard[],
  cardIndex: number,
  item: MenuItem,
  language: string
): any[] {
  const card = itemCards[cardIndex];
  if (!card || (!item.variants_and_options && !item.meals_and_combos)) {
    return [];
  }

  const extractedVariants: any[] = [];

  // Handle variants_and_options
  if (item.variants_and_options) {
    for (const group of item.variants_and_options) {
      const selectedValue = card.variants[group.id];

      if (selectedValue && Array.isArray(group.data)) {
        const selectedOption = group.data.find(
          (opt: any) => opt.name === selectedValue
        );
        if (selectedOption) {
          extractedVariants.push({
            optionName: getFormName(group, language),
            variantName: selectedOption.name,
            variantPrice: selectedOption.price || 0,
            isVariant: true,
            isMultiple: false,
            quantity: card.quantity,
          });
        }
      }
    }
  }

  // Handle meals_and_combos
  if (item.meals_and_combos) {
    for (const combo of item.meals_and_combos) {
      const selectedCombo = card.variants[combo.id];

      if (selectedCombo) {
        extractedVariants.push({
          optionName: getFormName(combo, language),
          variantName: getFormName(combo, language),
          variantPrice: combo.data?.combo_price || 0,
          isVariant: false,
          isMultiple: false,
          variantType: "meals_and_combos",
          actionType: combo.action_type || "upgrade",
          quantity: card.quantity,
          comboData: combo.data,
        });
      }
    }
  }

  return extractedVariants;
}
